// ABOUTME: Defines authenticated React Query cache boundaries for sensitive API data.
// ABOUTME: Prevents cached PII from crossing users, companies, or capability changes.
import type { QueryClient } from "react-query";

interface AuthenticatedQueryScopeInput {
    principal: string | null;
    companyId: string | number;
    canShowContactList: boolean;
}

interface WhatsappConversationQueryScopeInput {
    principal: string | null;
    companyId: string | number;
    canShowApplicants: boolean;
    hasJobAccess: boolean;
    whatsappEnabled: boolean;
    whatsappPreRegistrationEnabled: boolean;
    accessFingerprint: string;
}

interface WhatsappPreRegistrationAccessQueryScopeInput {
    principal: string | null;
    companyId: string | number;
    canShowApplicants: boolean;
    whatsappEnabled: boolean;
}

interface ApiCacheQueryKeyInput {
    mainKey: string;
    address: string;
    verb: string;
    params: Compleo.IObject;
    queryScope?: Compleo.IObject;
}

interface NoCompaniesSignOutInput {
    allCompanies?: unknown[];
    queryClient: QueryClient;
    signOut: () => Promise<unknown>;
}

type CacheAndSignOutInput = Omit<NoCompaniesSignOutInput, "allCompanies">;

export const getAuthenticatedQueryScope = ({
    principal,
    companyId,
    canShowContactList
}: AuthenticatedQueryScopeInput): Compleo.IObject => ({
    principal: principal || "anonymous",
    companyId: String(companyId),
    canShowContactList
});

export const getWhatsappConversationQueryScope = ({
    principal,
    companyId,
    canShowApplicants,
    hasJobAccess,
    whatsappEnabled,
    whatsappPreRegistrationEnabled,
    accessFingerprint
}: WhatsappConversationQueryScopeInput): Compleo.IObject => ({
    principal: principal || "anonymous",
    companyId: String(companyId),
    capability: {
        canShowApplicants,
        hasJobAccess,
        whatsappEnabled,
        whatsappPreRegistrationEnabled,
        accessFingerprint
    }
});

export const getWhatsappPreRegistrationAccessQueryScope = ({
    principal,
    companyId,
    canShowApplicants,
    whatsappEnabled
}: WhatsappPreRegistrationAccessQueryScopeInput): Compleo.IObject => ({
    principal: principal || "anonymous",
    companyId: String(companyId),
    capability: {
        canShowApplicants,
        whatsappEnabled
    }
});

export const getApiCacheQueryKey = ({
    mainKey,
    address,
    verb,
    params,
    queryScope
}: ApiCacheQueryKeyInput) => {
    const baseKey = [mainKey, address, verb, params];
    return queryScope === undefined
        ? baseKey
        : [...baseKey, { authenticatedQueryScope: queryScope }];
};

export const clearAuthenticatedQueryCache = async (
    queryClient: QueryClient
) => {
    await queryClient.cancelQueries();
    queryClient.clear();
};

export const clearWhatsappConversationQueryCache = async (
    queryClient: QueryClient
) => {
    await queryClient.cancelQueries("/whatsapp/listmessages");
    queryClient.removeQueries("/whatsapp/listmessages");
};

export const clearCacheAndSignOut = async ({
    queryClient,
    signOut
}: CacheAndSignOutInput) => {
    await clearAuthenticatedQueryCache(queryClient);
    try {
        await signOut();
    } finally {
        await clearAuthenticatedQueryCache(queryClient);
    }
};

export const clearCacheAndSignOutWhenNoCompanies = async ({
    allCompanies,
    queryClient,
    signOut
}: NoCompaniesSignOutInput): Promise<boolean> => {
    if ((allCompanies || []).length > 0) {
        return false;
    }

    await clearCacheAndSignOut({ queryClient, signOut });
    return true;
};
